import React, { useCallback, useEffect, useState, useRef } from "react";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { MAIN_URL, getApiHeaders } from "../../../Configurations/Urls";
import toast from "react-hot-toast";
import DescriptionIcon from "@mui/icons-material/Description";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import Layout4 from "../../DataLayouts/Layout4";
import { useNavigate, useSearchParams } from "react-router-dom";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import {
  USER_ROLE_DEFAULT_COLUMNS,
  USER_ROLES_DATAGRID_KEY,
  mapSavedUserRoleColumns,
  mapUserRoleRowForDisplay,
  resolveUserRoleApiField,
} from "./userRoleListConstants";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_user_role_id",
  sortOrder: "desc",
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({
        field: resolveUserRoleApiField(field),
        operator,
        value: filterValue,
      });
    }
  });

  return filters;
};

function UserRoleList() {
  const [userroles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(USER_ROLE_DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const { userData } = useAuthStore();
  const org = userData?.organization;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const datagridKey = `${USER_ROLES_DATAGRID_KEY}_${org.organization_id}`;
        const configRes = await fetch(`${MAIN_URL}/api/datagrid/get-by-context`, {
          method: "POST",
          headers: getApiHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({
            datagrid_key: datagridKey,
            organization_id: org.organization_id,
          }),
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const serverCfg =
            configResponse?.datagrid?.datagrid_default_configuration;

          if (serverCfg?.columns?.length > 0) {
            setTableConfig(serverCfg);
            setConfigColumns(mapSavedUserRoleColumns(serverCfg.columns));
          } else {
            setTableConfig(null);
            setConfigColumns(USER_ROLE_DEFAULT_COLUMNS);
          }
        } else {
          setTableConfig(null);
          setConfigColumns(USER_ROLE_DEFAULT_COLUMNS);
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setTableConfig(null);
        setConfigColumns(USER_ROLE_DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!hasInitializedURL.current) return;
    if (!org?.organization_id) {
      setLoading(false);
      setUserRoles([]);
      return;
    }

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: resolveUserRoleApiField(
              searchParams.get("sortBy") || "organization_user_role_id"
            ),
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/userroles-filtered`,
          {
            method: "POST",
            headers: getApiHeaders(),
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success && Array.isArray(result.data)) {
          setUserRoles(result.data.map((item) => mapUserRoleRowForDisplay(item)));

          if (result.pagination) {
            const pageSize =
              result.pagination.pageSize || result.pagination.limit || 10;
            setPaginationData({
              page:
                result.pagination.page ||
                Number.parseInt(searchParams.get("page"), 10) ||
                1,
              pageSize,
              total: result.pagination.total || 0,
              totalPages: result.pagination.totalPages || 1,
              hasNext: result.pagination.hasNext || false,
              hasPrev: result.pagination.hasPrev || false,
            });
          }
        } else {
          setUserRoles([]);
          setPaginationData(DEFAULT_PAGINATION);
        }
      } catch (error) {
        if (error.name === "AbortError") return;

        if (error.response?.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
          return;
        }
        console.error("Error fetching user roles:", error);
        toast.error("Failed to load user roles");
        setUserRoles([]);
        setPaginationData(DEFAULT_PAGINATION);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [searchParams, org?.organization_id]);

  const handleDelete = useCallback(
    async (id) => {
      if (!org?.organization_id) {
        toast.error("No active organization.");
        return;
      }
      try {
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/userrole/${id}`,
          {
            method: "DELETE",
            headers: getApiHeaders(),
          }
        );

        if (response.ok) {
          const result = await response.json();
          toast.success(result.message || "User Role deleted successfully");
          setSearchParams((prev) => new URLSearchParams(prev));
        } else {
          const errorData = await response.json();
          if (response.status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else {
            const errorMessage =
              errorData.message ||
              errorData.errors?.[Object.keys(errorData.errors || {})[0]]?.[0] ||
              "Failed to delete Role";
            toast.error(errorMessage);
          }
        }
      } catch (error) {
        console.error("Delete failed:", error);
        toast.error("Failed to delete User Roles");
      }
    },
    [org?.organization_id, setSearchParams]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(`/application/user-roles/edit/${item.id}`);
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(`/application/user-roles/view/${item.id}`);
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", resolveUserRoleApiField(field));
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  return (
    <>
      <Layout4
        loading={loading}
        delete_action={"USER_ROLE_DELETE"}
        heading={"User Roles"}
        btnName={"Add User Roles"}
        Data={userroles}
        Icons={[
          <ExitToAppIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <AdminPanelSettingsIcon color="primary" />,
          <DescriptionIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          " User Roles",
          " User Roles",
          "Add  User Roles",
          " User Roles",
        ]}
        Route={"/application/user-roles"}
        setData={setUserRoles}
        DeleteFunc={handleDelete}
      />

      <div className="ems-section-divider" />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="organization_roles"
          primaryKey="organization_user_role_id"
          heading="User Roles"
          data={userroles}
          paginationData={paginationData}
          filterModule="organization_roles"
          sortname="name"
          showActions={true}
          Route="/application/user-roles"
          DeleteFunc={handleDelete}
          EditFunc={handleEdit}
          edit_delete_action={["USER_ROLE_DELETE", "USER_ROLE_EDIT"]}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          token={localStorage.getItem("token")}
          configss={configColumns}
          loadedBackendConfig={tableConfig}
          loading={loading || loadingConfig}
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default UserRoleList;
